import React, { useEffect, useMemo, useState } from "react";
import { Portal } from "@/lib/api";
import { Search, Send, HelpCircle, ChevronDown, ChevronUp, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const PortalQA = () => {
  const [articles, setArticles] = useState([]);
  const [myQuestions, setMyQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");
  const [expanded, setExpanded] = useState(null);
  const [askSubject, setAskSubject] = useState("");
  const [askBody, setAskBody] = useState("");
  const [asking, setAsking] = useState(false);

  const refresh = async () => {
    try {
      const [a, mq] = await Promise.all([Portal.articles.list(), Portal.questions.mine()]);
      setArticles(a); setMyQuestions(mq);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Q&A");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const categories = useMemo(() => {
    const set = new Set(articles.map((a) => a.category || "General"));
    return ["All", ...Array.from(set).sort()];
  }, [articles]);

  const filtered = useMemo(() => {
    const needle = q.toLowerCase().trim();
    return articles.filter((a) => {
      if (category !== "All" && (a.category || "General") !== category) return false;
      if (!needle) return true;
      return (a.title + " " + a.body_html).toLowerCase().includes(needle);
    });
  }, [articles, q, category]);

  const submit = async () => {
    if (!askSubject.trim() || !askBody.trim()) {
      toast.error("Please add a subject and question");
      return;
    }
    setAsking(true);
    try {
      await Portal.questions.submit({ subject: askSubject.trim(), body: askBody.trim() });
      toast.success("Question sent — ATC will reply soon");
      setAskSubject(""); setAskBody("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally { setAsking(false); }
  };

  return (
    <section className="p-10" data-testid="portal-qa-tab">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-2">Portal</div>
        <h1 className="font-display font-black text-4xl text-white">Q &amp; A</h1>
        <p className="text-slate-400 text-sm mt-2">Browse our knowledge base — or ask ATC a question directly.</p>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,380px)] gap-6">
        <div>
          <div className="mb-5 flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search knowledge base…"
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-slate-950 border border-white/10 text-sm text-white placeholder:text-slate-500"
                data-testid="portal-qa-search"
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                    category === c ? "bg-atc-blue text-white" : "bg-slate-900/60 text-slate-300 border border-white/10 hover:border-white/25"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="text-slate-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="glass rounded-xl p-12 text-center text-slate-400" data-testid="portal-qa-empty">
              No articles yet — try asking a question on the right.
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((a) => {
                const isOpen = expanded === a.id;
                return (
                  <div key={a.id} className="glass rounded-xl overflow-hidden border border-white/5" data-testid={`portal-article-${a.id}`}>
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : a.id)}
                      className="w-full flex items-center gap-3 text-left px-5 py-4 hover:bg-white/[0.02]"
                    >
                      <HelpCircle className="h-4 w-4 text-atc-blue shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm font-semibold">{a.title}</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">{a.category || "General"}</div>
                      </div>
                      {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                    </button>
                    {isOpen && (
                      <div
                        className="px-5 pb-5 pl-12 text-sm text-slate-300 prose-styled leading-relaxed border-t border-white/5 pt-4"
                        dangerouslySetInnerHTML={{ __html: a.body_html }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="glass rounded-2xl p-6 space-y-3" data-testid="portal-ask-form">
            <div className="text-sm font-semibold text-white">Ask ATC a question</div>
            <input
              value={askSubject}
              onChange={(e) => setAskSubject(e.target.value)}
              placeholder="Subject"
              className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-white/10 text-sm text-white placeholder:text-slate-500"
              data-testid="portal-ask-subject"
            />
            <textarea
              rows={4}
              value={askBody}
              onChange={(e) => setAskBody(e.target.value)}
              placeholder="What would you like to know?"
              className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-white/10 text-sm text-white placeholder:text-slate-500"
              data-testid="portal-ask-body"
            />
            <div className="flex justify-end">
              <button
                onClick={submit}
                disabled={asking}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full brand-gradient text-white text-xs font-semibold hover:opacity-95 disabled:opacity-60"
                data-testid="portal-ask-submit"
              >
                <Send className="h-3.5 w-3.5" /> {asking ? "Sending…" : "Send question"}
              </button>
            </div>
          </div>

          <div className="glass rounded-2xl p-6" data-testid="portal-my-questions">
            <div className="text-sm font-semibold text-white mb-3">Your questions</div>
            {myQuestions.length === 0 ? (
              <div className="text-xs text-slate-500">No questions yet.</div>
            ) : (
              <div className="space-y-3">
                {myQuestions.map((qq) => (
                  <div key={qq.id} className="border border-white/5 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      {qq.status === "answered" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300"><CheckCircle2 className="h-3 w-3" /> Answered</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300"><Clock className="h-3 w-3" /> Waiting</span>
                      )}
                      <span className="text-[10px] text-slate-500 ml-auto">{new Date(qq.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="text-sm text-white font-medium">{qq.subject}</div>
                    <div className="text-xs text-slate-400 mt-1 whitespace-pre-wrap">{qq.body}</div>
                    {qq.reply && (
                      <div className="mt-3 pt-3 border-t border-white/5 text-xs text-atc-green whitespace-pre-wrap">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-atc-blue mb-1">ATC replied</div>
                        {qq.reply}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PortalQA;
